var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createContext, useMemo, useState } from 'react';
import { createTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getPalette } from './palette';
export var themeSettings = function (mode, fontFamily) {
    var _a;
    var palette = getPalette(mode, fontFamily);
    return {
        typography: {
            fontFamily: fontFamily,
            alignItems: 'center',
            display: 'flex',
            'button-sm': {
                fontSize: '13px',
                lineHeight: '22px',
            },
            'button-md': {
                fontSize: '14px',
                lineHeight: '24px',
            },
            'button-lg': {
                fontSize: '15px',
                lineHeight: '26px',
                letterSpacing: '0.46px',
            },
            'label-sm': {
                fontSize: '12px',
                lineHeight: '12px',
            },
            'label-md': {
                fontSize: '14px',
                lineHeight: '16px',
            },
            'label-lg': {
                fontSize: '14px',
                lineHeight: '16px',
            },
            'paragraph-xs': {
                fontSize: '10px',
                lineHeight: '12px',
            },
            'paragraph-sm': {
                fontSize: '12px',
                lineHeight: '16px',
            },
            'paragraph-md': {
                fontSize: '14px',
                lineHeight: '18px',
            },
            'paragraph-lg': {
                fontSize: '16px',
                lineHeight: '20px',
            },
            'paragraph-xl': {
                fontSize: '16px',
                lineHeight: '24px',
            },
            'headline-sm': {
                fontSize: '20px',
                lineHeight: '24px',
            },
            'headline-md': {
                fontSize: '24px',
                lineHeight: '28.8px',
                letterSpacing: '12%',
            },
            'headline-lg': {
                fontSize: '36px',
                lineHeight: '54px',
            },
            'sub-title': {
                fontSize: '18px',
            },
        },
        mixins: {
            toolbar: {
                minHeight: '74px',
            },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    colorPrimary: {
                        backgroundColor: palette.primary.contrastText,
                        boxShadow: 'none',
                        padding: '0 24px 0 24px',
                        color: palette.textIcon.primary,
                    },
                },
            },
            MuiButton: {
                variants: [
                    {
                        props: { size: 'large' },
                        style: {
                            padding: '8px 22px',
                        },
                    },
                    {
                        props: { size: 'medium' },
                        style: {
                            padding: '6px 16px',
                        },
                    },
                    {
                        props: { size: 'small' },
                        style: {
                            padding: '4px 10px',
                        },
                    },
                ],
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        width: 'fit-content',
                        height: 'fit-content',
                    },
                    containedPrimary: {
                        backgroundColor: palette.primary.main,
                        color: palette.primary.contrastText,
                        border: '1px solid',
                        borderColor: palette.primary.main,
                        '&.Mui-disabled': {
                            backgroundColor: palette.neutral[300],
                            border: '1px solid',
                            borderColor: palette.neutral[300],
                            color: palette.neutral[500],
                        },
                    },
                    outlinedSecondary: {
                        borderColor: palette.secondary.lightGray,
                        color: palette.secondary.lightGray,
                        ':hover': {
                            borderColor: palette.secondary.lightGray,
                        },
                        '&.Mui-disabled': {
                            color: palette.text.disabled,
                        },
                    },
                    outlinedPrimary: {
                        borderColor: palette.primary.main,
                    },
                    textPrimary: {
                        color: palette.primary.main,
                    },
                    textSecondary: {
                        color: palette.textIcon.primary,
                        '&.Mui-disabled': {
                            color: palette.text.disabled,
                        },
                    },
                    textWarning: {
                        color: palette.danger.main,
                    },
                    outlinedWarning: {
                        borderColor: palette.danger.secondary,
                        color: palette.danger.dark,
                        ':hover': {
                            borderColor: palette.danger.secondary,
                        },
                    },
                    containedWarning: {
                        backgroundColor: palette.danger.main,
                        color: palette.primary.contrastText,
                        ':hover': {
                            backgroundColor: palette.danger.main,
                        },
                    },
                },
            },
            MuiSelect: {
                defaultProps: {
                    IconComponent: KeyboardArrowDownIcon,
                },
            },
            MuiTableSortLabel: {
                defaultProps: {
                // IconComponent: SortIcon,
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.primary.main,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.primary.main,
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        padding: '4px',
                        '& .MuiCheckbox-root': {
                            padding: '6px',
                        },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        '& fieldset': { border: 'none' },
                        color: palette.textIcon.primary,
                    },
                    colorSecondary: {
                        color: palette.text.secondary,
                        backgroundColor: palette.secondary.main,
                    },
                    colorWarning: {
                        color: palette.danger.main,
                    },
                },
            },
            MuiDialog: {
                variants: [
                    {
                        props: { fullScreen: true },
                        style: {
                            '& .MuiDialogActions-root': {
                                borderTop: '1px solid',
                                borderTopColor: palette.border.container,
                                padding: '23px 25px 18px 26px',
                            },
                        },
                    },
                ],
                styleOverrides: {
                    paper: {
                        backgroundColor: palette.background.paper,
                        backgroundImage: 'unset',
                    },
                    root: {
                        '& .MuiBackdrop-root': {
                            backgroundColor: palette.overlay.main,
                        },
                        '& .MuiDialogActions-root': {
                            padding: '4px 24px 24px 24px',
                        },
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        color: palette.border.container,
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    clearIndicator: {
                        border: 'none',
                    },
                    popupIndicator: {
                        border: 'none',
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        '& .MuiInputLabel-asterisk': {
                            color: (_a = palette.danger) === null || _a === void 0 ? void 0 : _a.main,
                        },
                        display: 'flex',
                        alignItems: 'center',
                    },
                },
            },
            MuiCard: {
                variants: [
                    {
                        props: { variant: 'outlined' },
                        style: {
                            borderColor: palette.border.container,
                            borderRadius: '4px',
                            padding: '24px',
                        },
                    },
                ],
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                    },
                },
            },
        },
        palette: __assign({ mode: mode }, palette),
    };
};
export var useMode = function (_mode, _fontFamily) {
    var _a = useState(_mode), mode = _a[0], setMode = _a[1];
    var _b = useState(_fontFamily), fontFamily = _b[0], setFontFamily = _b[1];
    var colorMode = useMemo(function () { return ({
        changeColorMode: function (newTheme) {
            setMode(newTheme);
        },
        changeFontFamily: function (newFontFamily) {
            setFontFamily(newFontFamily);
        },
    }); }, []);
    var theme = useMemo(function () { return createTheme(themeSettings(mode, fontFamily)); }, [mode, fontFamily]);
    return [theme, colorMode];
};
export var ColorModeContext = createContext({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    changeColorMode: function (newTheme) { },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    changeFontFamily: function (newFont) { },
});
